import React from 'react';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

// English Cookie Policy section bodies — DRAFT translation pending legal
// review; docs/legal/cookie-policy.md (Italian) is the binding version.
// Mirrors cookie.it.jsx §§1-8, carrying the FULL substance of
// docs/legal/en/cookie-policy.md (see cookie.it.jsx's header comment for the
// two deliberate self-referential omissions, documented in
// task-C-report.md's fix-round-2 section).
function Tbl({ head, rows }) {
  return (
    <Table size="small" sx={{ mb: 2, '& td, & th': { verticalAlign: 'top', fontSize: '0.85rem' } }}>
      <TableHead>
        <TableRow>
          {head.map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) => <TableCell key={j}>{cell}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const cookieEn = {
  titolare: (
    <>
      <strong>[COMPANY NAME AND LEGAL FORM]</strong> — office at <strong>[ADDRESS]</strong> — email{' '}
      <code>info@fluel.it</code> — privacy contact <strong><code>[privacy@fluel.it]</code></strong>. For all
      information on the processing of personal data, please refer to the <strong>
        <Link component={RouterLink} to="/privacy">Privacy Notice</Link>
      </strong>.
    </>
  ),
  cosaSono: (
    <>
      <Typography sx={{ mb: 2 }}>
        <strong>Cookies</strong> are small text files that the sites a user visits send to the user's terminal
        device, where they are stored in order to be sent back to the same sites on the next visit. Similar
        tools (e.g. <em>local storage</em>, <em>session storage</em>, pixels, SDKs, identifiers) make it
        possible to store or access information on the user's device and are subject to the same rules.
      </Typography>
      <Typography sx={{ mb: 1 }}>Depending on their purpose, they are classified as:</Typography>
      <Box component="ul" sx={{ mb: 2 }}>
        <li><strong>Technical cookies/tools</strong> — necessary for the site to function and to provide a
          service explicitly requested by the user (e.g. authentication, security, load balancing, storing
          preferences). <strong>They do not require consent</strong> (Art. 122(1) of the Codice Privacy).</li>
        <li><strong>Measurement (analytics) cookies/tools</strong> — used to collect aggregate information on
          the use of the site. They are treated as equivalent to technical cookies <strong>only if</strong>{' '}
          suitably anonymized and first-party only, <strong>without being combined with other processing or
          transmitted to third parties</strong>; <strong>otherwise they require consent</strong>.</li>
        <li><strong>Profiling and marketing cookies/tools</strong> — create user profiles in order to send
          messages in line with the preferences expressed. <strong>They always require</strong> prior, freely
          given, specific, informed and revocable <strong>consent</strong>.</li>
      </Box>
      <Typography>
        Depending on who sets them, cookies are classified as <strong>first-party</strong> (set by the
        Controller) or <strong>third-party</strong> (set by other parties).
      </Typography>
    </>
  ),
  strumentiUsati: (
    <>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>3.1 Technical tools (no consent required)</Typography>
      <Tbl
        head={['Tool', 'Type / technology', 'Purpose', 'Duration', 'Managed by']}
        rows={[
          [
            'Firebase authentication token',
            'localStorage / sessionStorage (tool similar to cookies)',
            'Keeping the user signed in across pages and sessions; access security',
            'Until logout or token expiry; data in sessionStorage are cleared when the browser is closed',
            'Google (on behalf of the Controller)',
          ],
          [
            'Interface preferences',
            'localStorage',
            'Remembering non-essential settings chosen by the user (e.g. state of sections)',
            'Persistent until deleted',
            'Controller',
          ],
          [
            'Consent-status record (sg.consent)',
            'localStorage (not a cookie)',
            'Storing the choices expressed on the cookie banner (the "Statistics" category accepted/rejected)',
            '12 months, after which the banner is shown again automatically',
            'Controller',
          ],
          [
            'Typefaces (Montserrat, Rajdhani, Fraunces, Bricolage Grotesque, DM Sans)',
            "Static files (.woff2) served from the Service's own domain",
            "Correct display of the site's typography",
            'Browser cache',
            "Controller — the fonts are hosted on the Service's own domain, with no request to or IP-address disclosure to Google or any other third party",
          ],
        ]}
      />

      <Typography sx={{ fontWeight: 700, mb: 1, mt: 3 }}>3.2 Tools subject to consent, enabled by the user</Typography>
      <Tbl
        head={['Tool', 'Purpose', 'Data / effects', 'Consent', 'References']}
        rows={[
          [
            'Google Analytics 4 (ID G-FXG3L4ZPD9) — cookies _ga, _ga_<ID>, duration up to 2 years',
            'Statistics on site usage',
            'Measurement of site usage; managed by Google Ireland Ltd / Google LLC; transfer of data to the United States under the EU-US Data Privacy Framework and, where applicable, Standard Contractual Clauses',
            'Required — enabled only after consent given on the banner (the "Statistics" category); if consent is withdrawn, collection is disabled client-side, the _ga/_ga_<ID> cookies are deleted, and from the next page load onward the script is not loaded at all',
            <Link key="ga-ref" href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</Link>,
          ],
        ]}
      />

      <Typography sx={{ fontWeight: 700, mb: 1, mt: 3 }}>3.3 Tools NOT currently active (planned for the future)</Typography>
      <Typography sx={{ mb: 2 }}>
        The following tools are <strong>not active</strong> as of today. They will be activated <strong>only
        after consent has been collected</strong>, where required, and this page will be updated at the same
        time:
      </Typography>
      <Tbl
        head={['Tool', 'Purpose', 'Category', 'Consent']}
        rows={[
          [
            'Stripe (js.stripe.com)',
            'Payment processing and fraud prevention, if and when subscriptions are activated',
            'Technical for the payment function; some anti-fraud components may be subject to consent',
            'To be assessed upon activation',
          ],
        ]}
      />
    </>
  ),
  baseGiuridica: (
    <Box component="ul">
      <li>For <strong>technical tools</strong>: Art. 122(1) of the Codice Privacy, which allows their use
        without consent as they are necessary to provide the requested service; any related processing of
        personal data is based on the Controller's legitimate interest in ensuring operation and security
        (Art. 6(1)(f) GDPR) or on the performance of a contract (Art. 6(1)(b)).</li>
      <li>For <strong>non-anonymized measurement tools and profiling and marketing tools</strong>: the user's{' '}
        <strong>consent</strong> (Art. 122 of the Codice Privacy; Art. 6(1)(a) GDPR), collected via a banner
        and revocable at any time.</li>
    </Box>
  ),
  gestirePreferenze: (
    <>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>5.1 On the site</Typography>
      <Typography sx={{ mb: 2 }}>
        On first access, a <strong>consent banner</strong> appears, with three equally prominent choices —{' '}
        <strong>Accept all</strong>, <strong>Reject</strong>, <strong>Customise</strong> — plus an "X" close
        control that, on first access, behaves like Reject (if the banner is instead reopened from the
        "Cookie preferences" link while a choice is already on record, closing with "X" or "Esc" does not
        change the existing consent). No box is pre-ticked: the "Statistics (Google Analytics)" category is
        off until the user explicitly turns it on from "Customise". Rejecting does not restrict access to
        content. The choice is stored (see § 3.1, sg.consent) for 12 months, after which the banner is shown
        again. At any time, choices can be <strong>changed</strong> via the "Cookie preferences" link in the
        site's footer, which reopens the banner.
      </Typography>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>5.2 From the browser</Typography>
      <Typography sx={{ mb: 1 }}>
        Users can block or delete cookies and site data from their browser settings. Disabling technical
        tools may impair use of the site (in particular authentication). Instructions:
      </Typography>
      <Box component="ul" sx={{ mb: 1 }}>
        <li><Link href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</Link></li>
        <li><Link href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie" target="_blank" rel="noopener noreferrer">Mozilla Firefox</Link></li>
        <li><Link href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Apple Safari</Link></li>
        <li><Link href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</Link></li>
      </Box>
      <Typography>
        To clear localStorage/sessionStorage: use the browser's "Clear browsing data" / "Website data"
        function.
      </Typography>
    </>
  ),
  trasferimenti: (
    <>
      The use of third-party tools (e.g. Google) may involve the transfer of data to third countries. Such
      transfers take place on the basis of the safeguards set out in the <strong>
        <Link component={RouterLink} to="/privacy">Privacy Notice</Link>
      </strong> (§ 6), to which reference is made (adequacy decisions, including the EU-US Data Privacy
      Framework where applicable, or Standard Contractual Clauses with supplementary measures).
    </>
  ),
  diritti: (
    <>
      <Typography sx={{ mb: 1 }}>
        Users may exercise the rights provided for by Articles 15-22 GDPR (
        <strong>access, rectification, erasure, restriction, objection, portability</strong>) and withdraw
        consent at any time by writing to <strong><code>[privacy@fluel.it]</code></strong>.
      </Typography>
      <Typography>
        They also have the right to lodge a complaint with the <strong>Garante per la protezione dei dati
        personali</strong> (
        <Link href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</Link>
        ). For details, please refer to the <Link component={RouterLink} to="/privacy">Privacy Notice</Link>.
      </Typography>
    </>
  ),
  aggiornamenti: (
    <>
      <Typography sx={{ mb: 1 }}>
        This Cookie Policy may be amended if new tools are introduced or regulations change; if tools subject
        to consent are activated, the change will be accompanied by the banner being shown again.
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Controller: <strong>[COMPANY NAME]</strong>. See also the <Link component={RouterLink} to="/privacy">Privacy Notice</Link> and the Terms and Conditions of Service.
      </Typography>
    </>
  ),
};

export default cookieEn;
